import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../services/supabaseClient';
import { 
    Users, 
    Phone, 
    MessageSquare, 
    Paperclip, 
    Search,
    Clock,
    Activity,
    UserCheck,
    PhoneCall,
    Loader2
} from 'lucide-react';
import TagDisplayList from '../common/TagDisplayList';
import CopyablePhone from '../CopyablePhone';

const AgentDashboard = () => {
    const { authFetch } = useAuth();
    const [stats, setStats] = useState({
        agents: [],
        active_calls: [],
        active_conversations: [],
        recent_attachments: []
    });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTags, setSelectedTags] = useState([]);
    const [realtimeStatus, setRealtimeStatus] = useState({
        connected: false,
        loading: true
    });

    const availableTags = useMemo(() => {
        const tagMap = new Map();
        stats.active_conversations.forEach(conv => {
            if (conv.tags && Array.isArray(conv.tags)) {
                conv.tags.forEach(tag => {
                    if (tag && tag.id && !tagMap.has(tag.id)) {
                        tagMap.set(tag.id, tag);
                    }
                });
            }
        });
        return Array.from(tagMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [stats.active_conversations]);

    const toggleTagFilter = (tagId) => {
        setSelectedTags(prev => 
            prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
        );
    };

    const fetchInitialStats = async () => {
        setLoading(true);
        try {
            const res = await authFetch('/api/backoffice/stats/realtime');
            if (res.ok) {
                const data = await res.json();
                setStats(data);
            }
        } catch (error) {
            console.error("Error fetching realtime stats:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchRealtimeStatus = async () => {
        try {
            const res = await authFetch('/api/backoffice/stats/realtime-connection');
            if (res.ok) {
                const data = await res.json();
                setRealtimeStatus({ connected: data.connected, loading: false });
            }
        } catch (error) {
            console.error("Error fetching realtime status:", error);
            setRealtimeStatus({ connected: false, loading: false });
        }
    };

    useEffect(() => {
        fetchInitialStats();
        fetchRealtimeStatus();

        // Actualizar estado del realtime cada 10 segundos
        const statusInterval = setInterval(fetchRealtimeStatus, 10000);

        // REALTIME SUBSCRIPTIONS
        const agentsChannel = supabase
            .channel('dashboard-agents')
            .on('postgres_changes', { event: '*', schema: 'espuma', table: 'agents' }, (payload) => {
                if (payload.eventType === 'UPDATE') {
                    setStats(prev => ({
                        ...prev,
                        agents: prev.agents.map(a => a.id === payload.new.id ? { ...a, ...payload.new } : a)
                    }));
                } else if (payload.eventType === 'INSERT') {
                    setStats(prev => ({ ...prev, agents: [...prev.agents, payload.new] }));
                }
            })
            .subscribe();

        const callsChannel = supabase
            .channel('dashboard-calls')
            .on('postgres_changes', { event: '*', schema: 'espuma', table: 'calls' }, (payload) => {
                if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
                    // Refresh calls because we might need full data (joins) or just filter
                    // For simplicity and consistency, we can update local state
                    if (payload.new.status === 'CONNECTED' && !payload.new.end_time) {
                        setStats(prev => {
                            const exists = prev.active_calls.some(c => c.id === payload.new.id);
                            if (exists) {
                                return {
                                    ...prev,
                                    active_calls: prev.active_calls.map(c => c.id === payload.new.id ? { ...c, ...payload.new } : c)
                                };
                            }
                            return { ...prev, active_calls: [...prev.active_calls, payload.new] };
                        });
                    } else {
                        // Remove if ended
                        setStats(prev => ({
                            ...prev,
                            active_calls: prev.active_calls.filter(c => c.id !== (payload.new?.id || payload.old?.id))
                        }));
                    }
                }
            })
            .subscribe();

        const convsChannel = supabase
            .channel('dashboard-convs')
            .on('postgres_changes', { event: '*', schema: 'messaging', table: 'conversations' }, (payload) => {
                if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
                    if (payload.new.status === 'open') {
                        setStats(prev => {
                            const exists = prev.active_conversations.some(c => c.id === payload.new.id);
                            if (exists) {
                                return {
                                    ...prev,
                                    active_conversations: prev.active_conversations.map(c => c.id === payload.new.id ? { ...c, ...payload.new } : c)
                                };
                            }
                            return { ...prev, active_conversations: [...prev.active_conversations, payload.new] };
                        });
                    } else {
                        setStats(prev => ({
                            ...prev,
                            active_conversations: prev.active_conversations.filter(c => c.id !== (payload.new?.id || payload.old?.id))
                        }));
                    }
                }
            })
            .subscribe();

        const attsChannel = supabase
            .channel('dashboard-atts')
            .on('postgres_changes', { event: 'INSERT', schema: 'messaging', table: 'attachments' }, (payload) => {
                setStats(prev => ({
                    ...prev,
                    recent_attachments: [payload.new, ...prev.recent_attachments].slice(0, 100)
                }));
            })
            .subscribe();

        return () => {
            clearInterval(statusInterval);
            supabase.removeChannel(agentsChannel);
            supabase.removeChannel(callsChannel);
            supabase.removeChannel(convsChannel);
            supabase.removeChannel(attsChannel);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredAgents = useMemo(() => {
        let result = stats.agents;
        
        if (selectedTags.length > 0) {
            result = result.filter(a => {
                const agentConvs = stats.active_conversations.filter(c => c.assigned_agent_id === a.email);
                return agentConvs.some(conv => 
                    conv.tags && Array.isArray(conv.tags) && conv.tags.some(tag => selectedTags.includes(tag.id))
                );
            });
        }

        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            result = result.filter(a => 
                a.full_name?.toLowerCase().includes(lowerSearch) || 
                a.email?.toLowerCase().includes(lowerSearch)
            );
        }
        
        return result;
    }, [stats.agents, stats.active_conversations, searchTerm, selectedTags]);

    const activeAgentsCount = stats.agents.filter(a => a.state !== 'OFFLINE').length;
    
    const getStatusColor = (state) => {
        switch (state) {
            case 'AVAILABLE': return 'bg-emerald-500';
            case 'BUSY': return 'bg-amber-500';
            case 'AFTER_CALL_WORK': return 'bg-blue-500';
            default: return 'bg-slate-500';
        }
    };

    const getStatusLabel = (state) => {
        switch (state) {
            case 'AVAILABLE': return 'Disponible';
            case 'BUSY': return 'Ocupado';
            case 'AFTER_CALL_WORK': return 'Post-Llamada';
            case 'OFFLINE': return 'Offline';
            default: return state;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header with search */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-900/50 p-6 rounded-2xl border border-slate-800">
                <div className="flex items-center gap-3">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Activity className="text-primary" />
                            Dashboard Real-Time
                        </h1>
                        <p className="text-slate-400 text-sm">Actividad actual de los agentes en el contact center.</p>
                    </div>
                    {/* Indicador de Realtime Backend */}
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-950/50 rounded-lg border border-slate-800">
                        {realtimeStatus.loading ? (
                            <Loader2 size={12} className="animate-spin text-slate-500" />
                        ) : (
                            <div 
                                className={`w-2 h-2 rounded-full ${realtimeStatus.connected ? 'bg-emerald-500' : 'bg-red-500'} animate-pulse`}
                                title={realtimeStatus.connected ? 'Realtime Backend Conectado' : 'Realtime Backend Desconectado'}
                            />
                        )}
                        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">
                            Backend {realtimeStatus.connected ? 'Online' : 'Offline'}
                        </span>
                    </div>
                </div>
                <div className="relative w-full md:w-80">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                        type="text"
                        placeholder="Buscar agente..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-200 outline-none focus:ring-1 focus:ring-primary/50 transition-all"
                    />
                </div>
            </div>

            {/* Tag Filters */}
            {availableTags.length > 0 && (
                <div className="flex items-center flex-wrap gap-2 px-4 py-3 bg-slate-900/50 border border-slate-800 rounded-2xl">
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mx-1">Etiquetas Activas:</span>
                    {availableTags.map(tag => {
                        const isSelected = selectedTags.includes(tag.id);
                        return (
                            <button
                                key={tag.id}
                                onClick={() => toggleTagFilter(tag.id)}
                                className={`text-[11px] px-2.5 py-1 rounded-md font-medium transition-all ${
                                    isSelected ? 'ring-1 ring-white/50 opacity-100 shadow-sm' : 'opacity-60 hover:opacity-90'
                                }`}
                                style={{ 
                                    backgroundColor: isSelected ? tag.color : `${tag.color}33`, 
                                    color: isSelected ? '#fff' : tag.color 
                                }}
                            >
                                {tag.name}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard 
                    title="Agentes Online" 
                    value={activeAgentsCount} 
                    icon={UserCheck} 
                    color="text-emerald-500" 
                    bgColor="bg-emerald-500/10" 
                />
                <StatCard 
                    title="Llamadas Activas" 
                    value={stats.active_calls.length} 
                    icon={PhoneCall} 
                    color="text-primary" 
                    bgColor="bg-primary/10" 
                />
                <StatCard 
                    title="Chats Activos" 
                    value={stats.active_conversations.length} 
                    icon={MessageSquare} 
                    color="text-blue-500" 
                    bgColor="bg-blue-500/10" 
                />
                <StatCard 
                    title="Adjuntos (24h)" 
                    value={stats.recent_attachments.length} 
                    icon={Paperclip} 
                    color="text-amber-500" 
                    bgColor="bg-amber-500/10" 
                />
            </div>

            {/* Agents Table */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-950/50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                <th className="px-6 py-4">Agente</th>
                                <th className="px-6 py-4">Estado</th>
                                <th className="px-6 py-4">Llamada Activa</th>
                                <th className="px-6 py-4">WhatsApp Activo</th>
                                <th className="px-6 py-4">Adjuntos Recientes</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/50">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <Loader2 size={32} className="animate-spin text-primary" />
                                            <span className="text-slate-400 font-medium">Cargando datos...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredAgents.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                                        No se encontraron agentes activos.
                                    </td>
                                </tr>
                            ) : (
                                filteredAgents.map(agent => {
                                    const activeCall = stats.active_calls.find(c => c.agent_id === agent.id);
                                    const activeConv = stats.active_conversations.find(c => c.assigned_agent_id === agent.email);
                                    const agentAttachments = stats.recent_attachments.filter(a => a.assigned_agent_id === agent.email);

                                    return (
                                        <tr key={agent.id} className="hover:bg-slate-800/30 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 group-hover:border-slate-600">
                                                        <Users size={20} />
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-slate-200">{agent.full_name || 'Sin nombre'}</div>
                                                        <div className="text-xs text-slate-500">{agent.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className={`w-2.5 h-2.5 rounded-full ${getStatusColor(agent.state)} shadow-[0_0_8px_rgba(0,0,0,0.5)]`} />
                                                    <span className="text-sm font-medium text-slate-300">{getStatusLabel(agent.state)}</span>
                                                </div>
                                                {agent.last_state_change && (
                                                    <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
                                                        <Clock size={10} />
                                                        {new Date(agent.last_state_change).toLocaleTimeString()}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {activeCall ? (
                                                    <div className="flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-lg border border-primary/20 w-fit">
                                                        <Phone size={14} className="animate-pulse" />
                                                        <CopyablePhone
                                                            phone={activeCall.caller_phone || activeCall.callee_phone}
                                                            textClassName="text-sm font-bold font-mono"
                                                        />
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-600 text-xs">—</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {activeConv ? (
                                                    <div className="flex flex-col gap-2">
                                                        <div className="flex items-center gap-2 bg-blue-500/10 text-blue-400 px-3 py-1.5 rounded-lg border border-blue-500/20 w-fit">
                                                            <MessageSquare size={14} />
                                                            <span className="text-sm font-bold">Activo</span>
                                                        </div>
                                                        {activeConv.tags && activeConv.tags.length > 0 && (
                                                            <div className="mt-1">
                                                                <TagDisplayList tags={activeConv.tags} />
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-600 text-xs">—</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    {agentAttachments.length > 0 ? (
                                                        <div className="flex -space-x-2">
                                                            {agentAttachments.slice(0, 3).map((att, i) => (
                                                                <div key={att.id} className="w-8 h-8 rounded-lg bg-slate-800 border-2 border-slate-900 flex items-center justify-center text-amber-500 hover:z-10 transition-transform hover:scale-110 cursor-help" title={att.original_filename}>
                                                                    <Paperclip size={14} />
                                                                </div>
                                                            ))}
                                                            {agentAttachments.length > 3 && (
                                                                <div className="w-8 h-8 rounded-lg bg-slate-800 border-2 border-slate-900 flex items-center justify-center text-[10px] text-slate-400 font-bold">
                                                                    +{agentAttachments.length - 3}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-slate-600 text-xs">—</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-lg flex items-center justify-between">
        <div>
            <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1">{title}</p>
            <h3 className={`text-3xl font-bold ${color}`}>{value}</h3>
        </div>
        <div className={`p-3 rounded-xl ${bgColor}`}>
            <Icon size={24} className={color} />
        </div>
    </div>
);

export default AgentDashboard;
